import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import css from "./lesson.module.css";
import { getCSRFToken } from "../../lib/csrf";
import { useTranslation } from "react-i18next";
import { trackLessonOpened } from "../../lib/analytics";
import addInWords from "../../img/addInWords.mp3";
import { useLocation } from "react-router-dom";
import { saveTrialProgress, getTrialProgress } from "../../lib/trialLessonProgress";

const WordsComponent = ({ lesson, onActivityComplete }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentView, setCurrentView] = useState("transcript"); // 'transcript' or 'dictionary'
    const [activeWord, setActiveWord] = useState(null);
    
    const location = useLocation();
    const isTrialLesson = location.pathname === '/trial-lesson';
    
    // Load trial progress if available
    const trialWordsProgress = isTrialLesson ? getTrialProgress('words') : null;
    const [viewedWords, setViewedWords] = useState(new Set(trialWordsProgress?.viewedWords || []));

    const [voices, setVoices] = useState([]);
    const [wordExplanation, setWordExplanation] = useState(null);
    const [wordTranslation, setWordTranslation] = useState(null);
    const [wordExample, setWordExample] = useState(null);
    const [panelLoading, setPanelLoading] = useState(false);
    const [panelError, setPanelError] = useState(null);

    const [translationMap, setTranslationMap] = useState({});
    const [studentVocabularyEntries, setStudentVocabularyEntries] = useState([]);
    const [wordType, setWordType] = useState(null);

    const [dmTab, setDmTab] = useState("all");
    const [dmQuery, setDmQuery] = useState("");

    const [openLangDropdown, setOpenLangDropdown] = useState(null);
    const [wordLangs, setWordLangs] = useState({});

    const hoverTimeout = useRef(null);
    const hoveredWord = useRef(null);
    const [hoveredPhraseIndex, setHoveredPhraseIndex] = useState(null);

    const addSound = useMemo(() => new Audio(addInWords), []);
    const [showPlusOne, setShowPlusOne] = useState(false);
    const [flashDictBar, setFlashDictBar] = useState(false);

    // Translation cache and request management
    const translationCache = useRef(new Map());
    const activeRequests = useRef(new Map());
    const requestQueue = useRef(new Map());

    const { t, i18n } = useTranslation();
    const speechSynth = typeof window !== "undefined" ? window.speechSynthesis : null;

    // true якщо прийшли з roadMap (через navigate) або через пряме посилання з roadMap
    const isFromRoadMap = location.state?.from === "roadMap" || document.referrer.includes("roadMap");
    
    // Save viewed words to trial progress
    useEffect(() => {
        if (isTrialLesson && viewedWords.size > 0) {
            const progressData = {
                viewedWords: Array.from(viewedWords),
                translationMap,
                studentVocabularyEntries
            };
            console.log('💾 Saving words progress:', progressData);
            saveTrialProgress('words', progressData);
        }
    }, [viewedWords, isTrialLesson, translationMap, studentVocabularyEntries]);

    useEffect(() => {
        if (!speechSynth) return;
        const loadVoices = () => {
            const availableVoices = speechSynth.getVoices();
            if (availableVoices.length > 0) setVoices(availableVoices);
            else setTimeout(loadVoices, 500);
        };
        loadVoices();
        if (speechSynth.onvoiceschanged !== undefined) {
            speechSynth.onvoiceschanged = loadVoices;
        }
    }, [speechSynth]);

    // Click outside to close lang dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest(`.${css.wrLangWrap}`)) {
                setOpenLangDropdown(null);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const getCachedTranslation = (word) => {
        const currentLanguage = i18n.language || "en";
        const cacheKey = `translation_${word}_${currentLanguage}`;
        const cached = sessionStorage.getItem(cacheKey);
        return cached ? JSON.parse(cached) : null;
    };

    useEffect(() => {
        if (!activeWord) return;

        const handleClickOutside = (e) => {
            if (!e.target.closest(`.${css.wpanel}`)) {
                setActiveWord(null);
            }
        };

        // setTimeout щоб не спрацювало одразу при кліку на слово
        const timer = setTimeout(() => {
            document.addEventListener("click", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timer);
            document.removeEventListener("click", handleClickOutside);
        };
    }, [activeWord]);

    const setCachedTranslation = (word, data) => {
        try {
            const currentLanguage = i18n.language || "en";
            const cacheKey = `translation_${word}_${currentLanguage}`;
            sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (error) {
            // Ignore quota exceeded for now
        }
    };

    const fetchStudentVocabularyEntries = async () => {
        // Fetch ALL vocabulary to populate the dictionary properly
        let url = `/api/v1/student-vocabulary/`;
        let allResults = [];
        try {
            while (url) {
                const response = await axios.get(url, { withCredentials: true });
                if (response.data?.results) allResults = [...allResults, ...response.data.results];
                if (response.data?.next) {
                    const nextUrl = new URL(response.data.next);
                    url = nextUrl.pathname + nextUrl.search;
                } else {
                    url = null;
                }
            }
            setStudentVocabularyEntries(allResults);
            fetchTranslationsForEntries(allResults);
            setLoading(false);
        } catch (error) {
            console.error("Помилка отримання доданих слів:", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            // Load trial progress first
            if (isTrialLesson && trialWordsProgress) {
                if (trialWordsProgress.translationMap) {
                    setTranslationMap(trialWordsProgress.translationMap);
                }
                if (trialWordsProgress.studentVocabularyEntries) {
                    setStudentVocabularyEntries(trialWordsProgress.studentVocabularyEntries);
                    setLoading(false);
                    return; // Skip API call for trial lesson
                }
            }
            
            fetchStudentVocabularyEntries();
            return () => {
                translationCache.current.clear();
                activeRequests.current.clear();
                requestQueue.current.clear();
            };
        };
        
        fetchData();
    }, [isTrialLesson]);

    useEffect(() => {
        setTranslationMap({});
        translationCache.current.clear();
        activeRequests.current.clear();
        requestQueue.current.clear();
    }, [i18n.language]);

    const handleAddOrRemoveVocabulary = async (entryId, text) => {
        const csrfToken = getCSRFToken();
        
        // For trial lesson, just store locally
        if (isTrialLesson) {
            const existingEntry = studentVocabularyEntries.find(
                (item) => item.text.trim().toLowerCase() === text.trim().toLowerCase(),
            );
            
            if (existingEntry) {
                // Remove from local state
                setStudentVocabularyEntries((prev) =>
                    prev.filter((item) => item.text.trim().toLowerCase() !== text.trim().toLowerCase())
                );
                return { type: "deleted", data: {} };
            } else {
                // Add to local state
                const newEntry = {
                    id: Date.now(), // temporary ID
                    entry: entryId,
                    text: text,
                    translation: translationMap[text] || "Переклад відсутній",
                    usage_example: wordExample || "",
                };
                setStudentVocabularyEntries((prev) => [...prev, newEntry]);
                return { type: "added", data: newEntry };
            }
        }
        
        // Authenticated user - use API
        if (!csrfToken) return;
        try {
            const existingEntry = studentVocabularyEntries.find(
                (item) => item.entry === entryId || item.text.trim().toLowerCase() === text.trim().toLowerCase(),
            );

            if (existingEntry) {
                const deleteResponse = await axios.delete(`/api/v1/student-vocabulary/${existingEntry.id}/`, {
                    headers: { "X-CSRFToken": csrfToken, "Content-Type": "application/json" },
                    withCredentials: true,
                });
                setStudentVocabularyEntries((prev) =>
                    prev.filter((item) => item.entry !== entryId && item.id !== existingEntry.id),
                );
                return { type: "deleted", data: deleteResponse.data };
            } else {
                const addResponse = await axios.post(
                    "/api/v1/student-vocabulary/",
                    { entry: entryId },
                    {
                        headers: { "X-CSRFToken": csrfToken, "Content-Type": "application/json" },
                        withCredentials: true,
                    },
                );
                if (lesson?.id) trackLessonOpened(lesson.id);
                if (onActivityComplete) setTimeout(onActivityComplete, 2000);
                return {
                    type: "added",
                    data: {
                        id: addResponse.data.id,
                        entry: entryId,
                        text: text,
                        translation: addResponse.data.translation || "Переклад відсутній",
                    },
                };
            }
        } catch (error) {
            console.error("Помилка додавання/видалення:", error);
            throw error;
        }
    };

    const fetchTranslation = async (word) => {
        const currentLanguage = i18n.language || "en";
        const wordKey = `${word.toLowerCase().trim()}_${currentLanguage}`;

        if (translationCache.current.has(wordKey)) return translationCache.current.get(wordKey);
        const sessionCached = getCachedTranslation(word);
        if (sessionCached) {
            translationCache.current.set(wordKey, sessionCached);
            return sessionCached;
        }

        if (activeRequests.current.has(wordKey)) {
            return new Promise((resolve, reject) => {
                if (!requestQueue.current.has(wordKey)) requestQueue.current.set(wordKey, []);
                requestQueue.current.get(wordKey).push({ resolve, reject });
            });
        }

        const requestPromise = new Promise(async (resolve, reject) => {
            try {
                const csrfToken = getCSRFToken();
                if (!csrfToken) throw new Error("CSRF токен відсутній");
                const response = await axios.post(
                    `/api/v1/videoLesson/${lesson.id}/translate/`,
                    { text: word },
                    {
                        headers: { "X-CSRFToken": csrfToken, "Content-Type": "application/json" },
                        withCredentials: true,
                    },
                );
                if (response.data) {
                    translationCache.current.set(wordKey, response.data);
                    setCachedTranslation(word, response.data);
                    resolve(response.data);
                } else {
                    throw new Error("Не вдалося отримати дані");
                }
            } catch (error) {
                if (error.response) {
                    console.log("Error Response Body:", error.response.data);
                    console.log("Status Code:", error.response.status);
                    console.log("Headers:", error.response.headers);
                }
            }
        });

        activeRequests.current.set(wordKey, requestPromise);
        try {
            const result = await requestPromise;
            if (requestQueue.current.has(wordKey)) {
                requestQueue.current.get(wordKey).forEach(({ resolve }) => resolve(result));
                requestQueue.current.delete(wordKey);
            }
            return result;
        } catch (error) {
            if (requestQueue.current.has(wordKey)) {
                requestQueue.current.get(wordKey).forEach(({ reject }) => reject(error));
                requestQueue.current.delete(wordKey);
            }
            throw error;
        } finally {
            activeRequests.current.delete(wordKey);
        }
    };

    const fetchTranslationsForEntries = async (entries) => {
        const updatedEntries = await Promise.all(
            entries.map(async (item) => {
                try {
                    const translationData = await fetchTranslation(item.text);
                    return {
                        ...item,
                        translation: translationData?.translation || "Переклад відсутній",
                        usage_example: translationData?.usage_example || "",
                    };
                } catch (error) {
                    return { ...item, translation: "Переклад відсутній", usage_example: "" };
                }
            }),
        );
        setStudentVocabularyEntries(updatedEntries);
    };

    const handleWordClick = async (word, isHover = false) => {
        if (!isHover) {
            setActiveWord(word);
            setViewedWords((prev) => new Set(prev).add(word));
            setPanelLoading(true);
            setPanelError(null);
        }

        try {
            const translationData = await fetchTranslation(word);
            setTranslationMap((prev) => ({ ...prev, [word]: translationData.translation || "Переклад відсутній" }));

            if (!isHover) {
                setWordTranslation(translationData.translation || "Переклад відсутній");
                setWordExample(translationData.usage_example || "Приклад відсутній");
                setWordExplanation(translationData.explanation || "Пояснення відсутнє");
                setWordType(translationData.type || null);
                setPanelLoading(false);
            }
        } catch (error) {
            if (!isHover) {
                setPanelError(error.message || "Помилка при запиті");
                setPanelLoading(false);
            }
        }
    };

    const handleMouseEnter = (word, index) => {
        hoveredWord.current = word;
        hoverTimeout.current = setTimeout(() => {
            if (hoveredWord.current === word) {
                setHoveredPhraseIndex(index); // ← index замість слова
                handleWordClick(word, true);
            }
        }, 500);
    };

    const handleMouseLeave = () => {
        hoveredWord.current = null;
        setHoveredPhraseIndex(null); // ← скидаємо index
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
            hoverTimeout.current = null;
        }
    };

    const toggleBookmark = async () => {
        if (!activeWord) return;
        try {
            const cached = getCachedTranslation(activeWord);
            const entryId = cached?.id;
            if (!entryId) return;

            const result = await handleAddOrRemoveVocabulary(entryId, activeWord);
            if (result.type === "added") {
                addSound.play();
                setShowPlusOne(true);
                setFlashDictBar(true);
                setTimeout(() => setShowPlusOne(false), 1000);
                setTimeout(() => setFlashDictBar(false), 500);

                setStudentVocabularyEntries((prev) => {
                    const filtered = prev.filter(
                        (item) => item.text.trim().toLowerCase() !== activeWord.trim().toLowerCase(),
                    );
                    return [
                        ...filtered,
                        {
                            id: result.data.id,
                            entry: entryId,
                            text: activeWord,
                            translation: wordTranslation || "Переклад відсутній",
                            usage_example: wordExample || "",
                        },
                    ];
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const playWord = (word) => {
        if (!speechSynth || voices.length === 0) return;
        const utterance = new SpeechSynthesisUtterance(word);
        const selectedVoice = voices.find((voice) => voice.lang.startsWith("en")) || voices[0];
        if (selectedVoice) utterance.voice = selectedVoice;
        speechSynth.cancel();
        speechSynth.speak(utterance);
    };

    const renderTranscription = () => {
        const fullText = lesson.video.transcription;
        const phrases = lesson.video.split_transcription;
        let currentIndex = 0;
        const elements = [];
        const entriesTextMap = new Set(studentVocabularyEntries.map((item) => item.text.trim().toLowerCase()));

        phrases.forEach((phrase, i) => {
            const safePhrase = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const regex = new RegExp(safePhrase, "i");
            const match = regex.exec(fullText.slice(currentIndex));

            if (match) {
                const start = currentIndex + match.index;
                const end = start + match[0].length;
                const beforeText = fullText.slice(currentIndex, start);
                const phraseText = fullText.slice(start, end);

                if (beforeText) elements.push(<span key={`before-${i}`}>{beforeText}</span>);

                const isSaved = entriesTextMap.has(phraseText.trim().toLowerCase());
                const isViewed = viewedWords.has(phraseText);
                const isActive = activeWord === phraseText;

                let hwClass = css.hw;
                if (isActive) hwClass += ` ${css.active}`;
                if (isSaved) hwClass += ` ${css.dict}`;
                else if (isViewed) hwClass += ` ${css.viewed}`;

                elements.push(
                    <span
                        key={`phrase-${i}`}
                        className={hwClass}
                        onClick={() => handleWordClick(phraseText, false)}
                        onMouseEnter={() => handleMouseEnter(phraseText, i)} // ← передаємо i
                        onMouseLeave={handleMouseLeave}>
                        {phraseText}
                        {hoveredPhraseIndex === i && ( // ← порівнюємо по i
                            <span className={css.hwTip}>{translationMap?.[phraseText] || "Переклад"}</span>
                        )}
                    </span>,
                );
                currentIndex = end;
            }
        });

        if (currentIndex < fullText.length) {
            elements.push(<span key="last">{fullText.slice(currentIndex)}</span>);
        }

        return elements;
    };

    const isWordSaved = (word) => {
        return studentVocabularyEntries.some((item) => item.text.trim().toLowerCase() === word.trim().toLowerCase());
    };

    const isWordFromThisLesson = (word) => {
        const transcriptPhrases = lesson?.video?.split_transcription || [];
        return transcriptPhrases.some((phrase) => phrase.toLowerCase().trim() === word.trim().toLowerCase());
    };

    const filteredDictList = studentVocabularyEntries.filter((w) => {
        if (isFromRoadMap) {
            if (dmTab === "lesson" && !isWordFromThisLesson(w.text)) return false;
        } else {
            if (dmTab === "lesson" && !isWordFromThisLesson(w.text)) return false;
        }

        if (dmQuery) {
            const q = dmQuery.toLowerCase();
            return w.text.toLowerCase().includes(q) || (w.translation && w.translation.toLowerCase().includes(q));
        }
        return true;
    });

    const lessonDictCount = studentVocabularyEntries.filter((w) => isWordFromThisLesson(w.text)).length;

    return (
        <div style={{ width: "100%" }}>
            {/* DICT BAR */}
            <div className={`${css.dictBar} ${flashDictBar ? css.flash : ""}`}>
                <div className={css.dbLeft}>
                    <div className={css.dbIcon}>
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                        </svg>
                    </div>
                    <div>
                        <div className={css.dbTitle}>{t("Мій словник")}</div>
                        <div className={css.dbSub}>{t("Додавайте слова з уроку та повторюйте їх")}</div>
                    </div>
                </div>
                <div className={css.dbRight}>
                    <div className={css.dbCountWrap}>
                        <div className={css.dbCount}>{lessonDictCount}</div>
                        {showPlusOne && <div className={`${css.dictPlus} ${css.pop}`}>+1</div>}
                    </div>
                    <div className={css.dbLabel}>{t("слів")}</div>
                    <button
                        className={css.dbOpen}
                        onClick={() => {
                            setDmTab("all");
                            setCurrentView("dictionary");
                        }}>
                        {t("Відкрити")}
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* MAIN CARD */}
            <div className={css.mainCard}>
                {/* VIEW 1: TRANSCRIPT */}
                <div className={`${css.view} ${currentView === "transcript" ? css.active : ""}`}>
                    <div className={css.mcInner}>
                        <div className={css.transcript}>
                            <div className={css.trTitle}>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                                {t("Текст з відео")}
                            </div>
                            <div className={css.trText}>
                                {lesson?.video?.transcription && lesson?.video?.split_transcription ? (
                                    renderTranscription()
                                ) : (
                                    <p>ℹ️ Немає транскрипції для цього уроку.</p>
                                )}
                            </div>
                        </div>

                        <div className={css.wpanel}>
                            {!activeWord ? (
                                <div className={css.wpEmpty}>
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.3-4.3" />
                                    </svg>
                                    <p>
                                        {t(
                                            "Натисніть на підкреслене слово, щоб побачити переклад, приклад і додати його у словник.",
                                        )}
                                    </p>
                                </div>
                            ) : panelLoading ? (
                                <div className={css.wpEmpty}>
                                    <p>{t("Завантаження...")}</p>
                                </div>
                            ) : panelError ? (
                                <div className={css.wpEmpty}>
                                    <p className={css.error}>{panelError}</p>
                                </div>
                            ) : (
                                <div className={`${css.wpContent} ${css.show}`}>
                                    <div className={css.wpHeader}>
                                        <div className={css.wpWord}>
                                            <span>{activeWord}</span>
                                            <span className={css.tag}>English</span>
                                        </div>
                                        <div className={css.wpHeaderActions}>
                                            <div
                                                className={css.wrBtn}
                                                onClick={() => playWord(activeWord)}
                                                title="Прослухати слово">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round">
                                                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                                </svg>
                                            </div>
                                            <div
                                                className={`${css.wpIconBtn} ${isWordSaved(activeWord) ? css.bookmarked : ""}`}
                                                onClick={toggleBookmark}
                                                title={
                                                    isWordSaved(activeWord)
                                                        ? "Прибрати зі словника"
                                                        : "Додати у словник"
                                                }>
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill={isWordSaved(activeWord) ? "currentColor" : "none"}
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round">
                                                    <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                                                </svg>
                                            </div>
                                            <div
                                                className={`${css.wpIconBtn} ${css.wpClose}`}
                                                onClick={() => setActiveWord(null)}
                                                title="Закрити">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round">
                                                    <path d="M18 6 6 18" />
                                                    <path d="m6 6 12 12" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`${css.wpSavedBadge} ${isWordSaved(activeWord) ? css.show : ""}`}>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                        У словнику
                                    </div>

                                    <div className={css.wpTrans}>{wordTranslation}</div>

                                    {wordExplanation && (
                                        <div className={css.wpSection}>
                                            <div className={css.wpLabel}>{t("Пояснення")}</div>
                                            <div className={css.wpText}>{wordExplanation}</div>
                                        </div>
                                    )}

                                    {wordExample && (
                                        <div className={css.wpSection}>
                                            <div className={css.wpLabel}>{t("Приклад")}</div>
                                            <div className={css.wpExample}>"{wordExample}"</div>
                                        </div>
                                    )}

                                    <div className={css.wpHint}>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                                        </svg>
                                        {t("Натисніть на закладку, щоб зберегти слово")}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* VIEW 2: DICTIONARY INLINE */}
                <div className={`${css.view} ${currentView === "dictionary" ? css.active : ""}`}>
                    <div className={`${css.dv} ${css.viewFade}`}>
                        <button className={css.dvBack} onClick={() => setCurrentView("transcript")}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="m12 19-7-7 7-7" />
                            </svg>
                            {t("Назад до тексту")}
                        </button>
                        <div className={css.dvHead}>
                            <svg
                                style={{ width: "22px", height: "22px", color: "var(--indigo-500)" }}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                            </svg>
                            <div className={css.dvTitle}>{t("Мій словник")}</div>
                        </div>
                        <div className={css.dvSub}>{t("Слова, які ви додали під час уроків.")}</div>

                        {isFromRoadMap && (
                            <div className={css.dvStats}>
                                <div className={css.dvSt}>
                                    <div className={css.dvStV}>{lessonDictCount}</div>
                                    <div className={css.dvStL}>{t("з цього уроку")}</div>
                                </div>
                                <div className={css.dvSt}>
                                    <div className={css.dvStV}>{studentVocabularyEntries.length}</div>
                                    <div className={css.dvStL}>{t("зі всього курсу")}</div>
                                </div>
                            </div>
                        )}

                        <div className={css.dvSearch}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.3-4.3" />
                            </svg>
                            <input
                                type="text"
                                placeholder={t("Пошук слова...")}
                                value={dmQuery}
                                onChange={(e) => setDmQuery(e.target.value)}
                            />
                        </div>
                        {!location.pathname.includes("/trial-lesson") && (
                            <div className={css.dvTabs}>
                                {isFromRoadMap ? (
                                    <>
                                        <button
                                            className={`${css.dvTab} ${dmTab === "all" ? css.on : ""}`}
                                            onClick={() => setDmTab("all")}>
                                            {t("Всі з курсу")}
                                        </button>
                                        <button
                                            className={`${css.dvTab} ${dmTab === "lesson" ? css.on : ""}`}
                                            onClick={() => setDmTab("lesson")}>
                                            {t("З цього уроку")}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            className={`${css.dvTab} ${dmTab === "all" ? css.on : ""}`}
                                            onClick={() => setDmTab("all")}>
                                            {t("Всі слова вивчені самостійно")}
                                        </button>
                                        <button
                                            className={`${css.dvTab} ${dmTab === "lesson" ? css.on : ""}`}
                                            onClick={() => setDmTab("lesson")}>
                                            {t("З цього відео")}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                        <div className={css.dvList}>
                            {filteredDictList.length === 0 ? (
                                <div className={css.dvEmpty}>
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.3-4.3" />
                                    </svg>
                                    <p>{t("Нічого не знайдено")}</p>
                                    <span>{t("Спробуйте інше слово або змініть фільтр.")}</span>
                                </div>
                            ) : (
                                filteredDictList.map((w, i) => (
                                    <div key={w.id} className={css.wr} style={{ animationDelay: `${i * 0.04}s` }}>
                                        <div className={css.wrTop}>
                                            <span className={css.wrEn}>{w.text}</span>
                                            <div className={css.wrActs}>
                                                <div
                                                    className={css.wrBtn}
                                                    onClick={() => playWord(w.text)}
                                                    title="Прослухати слово">
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round">
                                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                                    </svg>
                                                </div>

                                                <div className={css.wrLangWrap}>
                                                    <div
                                                        className={css.wrBtn}
                                                        onClick={() =>
                                                            setOpenLangDropdown(openLangDropdown === w.id ? null : w.id)
                                                        }
                                                        title="Мова перекладу">
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round">
                                                            <circle cx="12" cy="12" r="10" />
                                                            <path d="M2 12h20" />
                                                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                                        </svg>
                                                    </div>
                                                    <div
                                                        className={`${css.wrLangDd} ${openLangDropdown === w.id ? css.show : ""}`}>
                                                        <div
                                                            className={`${css.wrLangOpt} ${wordLangs[w.id] === "ua" || !wordLangs[w.id] ? css.on : ""}`}
                                                            onClick={() => {
                                                                setWordLangs((prev) => ({ ...prev, [w.id]: "ua" }));
                                                                setOpenLangDropdown(null);
                                                            }}>
                                                            <span className={css.lflag}>🇺🇦</span>Українська
                                                        </div>
                                                        <div
                                                            className={`${css.wrLangOpt} ${wordLangs[w.id] === "pl" ? css.on : ""}`}
                                                            onClick={() => {
                                                                setWordLangs((prev) => ({ ...prev, [w.id]: "pl" }));
                                                                setOpenLangDropdown(null);
                                                            }}>
                                                            <span className={css.lflag}>🇵🇱</span>Polski
                                                        </div>
                                                        {/* <div className={`${css.wrLangOpt} ${wordLangs[w.id] === 'de' ? css.on : ''}`} onClick={() => {setWordLangs(prev => ({...prev, [w.id]: 'de'})); setOpenLangDropdown(null);}}>
                              <span className={css.lflag}>🇩🇪</span>Deutsch
                            </div>
                            <div className={`${css.wrLangOpt} ${wordLangs[w.id] === 'es' ? css.on : ''}`} onClick={() => {setWordLangs(prev => ({...prev, [w.id]: 'es'})); setOpenLangDropdown(null);}}>
                              <span className={css.lflag}>🇪🇸</span>Español
                            </div>*/}
                                                    </div>
                                                </div>

                                                <div
                                                    className={`${css.wrBtn} ${css.del}`}
                                                    onClick={() => handleAddOrRemoveVocabulary(w.entry, w.text)}
                                                    title="Прибрати зі словника">
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round">
                                                        <path d="M3 6h18" />
                                                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={css.wrUa}>{w.translation}</div>

                                        <div className={css.wrMeta}>
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round">
                                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                                            </svg>
                                            {isWordFromThisLesson(w.text)
                                                ? lesson?.video?.title || "Поточний урок"
                                                : w.video_title || "Інший урок"}
                                        </div>

                                        {w.usage_example && <div className={css.wrEx}>"{w.usage_example}"</div>}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WordsComponent;
